/** Zustandsverwaltung für das Travel-Menü: Daten laden, Fortschritt, Flotte senden. */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { TravelBarData } from './TravelProgressBar';
import {
  SHIP_COLUMNS, type TravelLocation, type ActiveTravel, type OwnPlanet, type RawTravel, type OtherPlanetShips, computeWarpInfo,
} from './TravelMenuTypes';

// Re-exports für bestehende Imports
export { SHIP_COLUMNS, SHIP_NAMES, TRAVEL_MODE_LABELS } from './TravelMenuTypes';
export type { TravelLocation, ActiveTravel, OwnPlanet, OtherPlanetShips } from './TravelMenuTypes';

export class TravelMenuState {
  ownPlanets: OwnPlanet[] = [];
  activeTravels: ActiveTravel[] = [];
  otherTravels: ActiveTravel[] = [];
  otherPlanetShips: OtherPlanetShips[] = []; // reserved for future scouting
  selectedStart: TravelLocation | null = null;
  selectedEnd: TravelLocation | null = null;
  shipSliders: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
  selectedMode = 0;
  isLoading = false;
  errorMsg = '';
  selectingOnMap: 'start' | 'end' | null = null;
  stationedSourceId: string | null = null;

  constructor(private _sb: SupabaseClient, private _worldId: string, private _playerId: string) {}

  async loadOwnPlanets(): Promise<void> {
    const { data: planets } = await this._sb.from('planets').select('id, axial_q, axial_r, planet_type, seed').eq('claimed_by', this._playerId);
    if (!planets) { this.ownPlanets = []; return; }
    const results: OwnPlanet[] = [];
    for (const p of planets) {
      const { data: ships } = await this._sb.from('planet_ships').select(SHIP_COLUMNS.join(', ')).eq('planet_id', p.id).maybeSingle();
      const s = ships as Record<string, number> | null;
      const composition: number[] = [];
      for (let i = 0; i < SHIP_COLUMNS.length; i++) composition.push(s?.[SHIP_COLUMNS[i]] ?? 0);
      results.push({ id: p.id, label: `${p.planet_type} (${p.axial_q},${p.axial_r})`, q: p.axial_q, r: p.axial_r, ships: composition });
    }
    this.ownPlanets = results;
  }

  async loadActiveTravels(): Promise<void> {
    const res = await this._sb.functions.invoke('get-active-travels', { body: { world_id: this._worldId } });
    if (res.error || !res.data) { this.activeTravels = []; this.otherTravels = []; this.otherPlanetShips = []; return; }
    const body = res.data as { travels: RawTravel[]; other_travels?: RawTravel[]; other_planet_ships?: OtherPlanetShips[] };
    this.activeTravels = (body.travels ?? []).map(t => _parseTravel(t));
    this.otherTravels = (body.other_travels ?? []).map(t => _parseTravel(t));
    this.otherPlanetShips = body.other_planet_ships ?? [];
    if (this.otherTravels.length > 0) console.log('[TravelMenuState] otherTravels loaded:', this.otherTravels.length, this.otherTravels.map(t => `${t.id}:${t.status}`));
  }

  get otherTravelingTravels(): ActiveTravel[] {
    const out: ActiveTravel[] = [];
    for (let i = 0; i < this.otherTravels.length; i++) { const t = this.otherTravels[i]; if (t.status === 'traveling' && !t.arrived) out.push(t); }
    return out;
  }
  get otherStationedTravels(): ActiveTravel[] {
    const out: ActiveTravel[] = [];
    for (let i = 0; i < this.otherTravels.length; i++) { const t = this.otherTravels[i]; if (t.status === 'stationed' || (t.status === 'traveling' && t.arrived)) out.push(t); }
    return out;
  }

  get travelingTravels(): ActiveTravel[] {
    const out: ActiveTravel[] = [];
    for (let i = 0; i < this.activeTravels.length; i++) { const t = this.activeTravels[i]; if (t.status === 'traveling' && !t.arrived) out.push(t); }
    return out;
  }
  get stationedTravels(): ActiveTravel[] {
    const out: ActiveTravel[] = [];
    for (let i = 0; i < this.activeTravels.length; i++) { const t = this.activeTravels[i]; if (t.status === 'stationed' || (t.status === 'traveling' && t.arrived)) out.push(t); }
    return out;
  }

  updateProgress(): boolean {
    const now = Date.now();
    let anyArrived = false;
    for (let i = 0; i < this.activeTravels.length; i++) {
      const t = this.activeTravels[i];
      if (t.arrived || t.status === 'stationed') continue;
      const started = Date.parse(t.startedAt);
      const arrival = Date.parse(t.estimatedArrivalAt);
      const total = arrival - started;
      if (total <= 0) { t.progress = 1; t.arrived = true; anyArrived = true; continue; }
      t.progress = Math.min((now - started) / total, 1);
      if (t.progress >= 1) { t.arrived = true; anyArrived = true; }
    }
    return anyArrived;
  }

  getBarData(travel: ActiveTravel): TravelBarData {
    const diff = new Date(travel.estimatedArrivalAt).getTime() - Date.now();
    let eta: string;
    if (diff <= 0) { eta = 'Angekommen'; }
    else { const mins = Math.floor(diff / 60_000); const secs = Math.floor((diff % 60_000) / 1000); eta = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`; }
    return { startIsPlanet: travel.start.isPlanet, endIsPlanet: travel.end.isPlanet, endIsSun: travel.end.isSun, progress: travel.progress, label: `\u2192 ${travel.end.label}`, eta };
  }

  selectStartPlanet(planet: OwnPlanet): void {
    this.selectedStart = { planetId: planet.id, q: planet.q, r: planet.r, label: planet.label, isPlanet: true, isSun: false };
    this.stationedSourceId = null;
    this.shipSliders = [0, 0, 0, 0, 0, 0, 0, 0];
  }

  get maxShips(): number[] {
    if (this.stationedSourceId) { const f = this.activeTravels.find(t => t.id === this.stationedSourceId); return f?.ships ?? [0, 0, 0, 0, 0, 0, 0, 0]; }
    if (!this.selectedStart?.planetId) return [0, 0, 0, 0, 0, 0, 0, 0];
    const p = this.ownPlanets.find(x => x.id === this.selectedStart!.planetId);
    return p?.ships ?? [0, 0, 0, 0, 0, 0, 0, 0];
  }

  get totalSelectedShips(): number { let t = 0; for (let i = 0; i < 8; i++) t += this.shipSliders[i]; return t; }
  get canSend(): boolean { return !!this.selectedStart && !!this.selectedEnd && this.totalSelectedShips > 0 && !this.isLoading && (!!this.selectedStart.planetId || !!this.stationedSourceId); }
  selectAllShips(): void { const max = this.maxShips; for (let i = 0; i < 8; i++) this.shipSliders[i] = max[i]; }
  clearAllShips(): void { this.shipSliders = [0, 0, 0, 0, 0, 0, 0, 0]; }
  getWarpInfo() { return computeWarpInfo(this.shipSliders); }

  async sendFleet(totalHops: number, route?: number[][]): Promise<boolean> {
    if (!this.canSend || !this.selectedStart || !this.selectedEnd) return false;
    this.isLoading = true; this.errorMsg = '';
    const res = await this._sb.functions.invoke('send-fleet', {
      body: { planet_id: this.selectedStart.planetId ?? null, stationed_source_id: this.stationedSourceId ?? null, end_q: this.selectedEnd.q, end_r: this.selectedEnd.r, end_planet_id: this.selectedEnd.planetId ?? null, end_sun_hex_key: this.selectedEnd.sunHexKey ?? null, travel_mode: this.selectedMode, ships: this.shipSliders, world_id: this._worldId, total_hops: totalHops, route: route ?? null },
    });
    this.isLoading = false;
    if (res.error) {
      let msg = 'Fehler beim Senden';
      try { const b = (res.data as { error?: string })?.error; if (b) msg = b; else if ('context' in res.error) { const parsed = await (res.error as { context: { json: () => Promise<{ error?: string }> } }).context.json(); if (parsed?.error) msg = parsed.error; } } catch { /* fallback */ }
      this.errorMsg = msg; return false;
    }
    this.clearAllShips(); this.stationedSourceId = null;
    // Refresh data in background — don't block the UI
    Promise.all([this.loadOwnPlanets(), this.loadActiveTravels()]).catch(() => {});
    return true;
  }
}

function _parseTravel(t: RawTravel): ActiveTravel {
  return {
    id: t.id,
    start: { planetId: t.start_planet_id, q: t.start_q, r: t.start_r, label: t.start_planet_id ? `Planet (${t.start_q},${t.start_r})` : `Hex (${t.start_q},${t.start_r})`, isPlanet: !!t.start_planet_id, isSun: false, sunHexKey: null },
    end: { planetId: t.end_planet_id, q: t.end_q, r: t.end_r, label: t.end_sun_hex_key ? `Sonne (${t.end_q},${t.end_r})` : t.end_planet_id ? `Planet (${t.end_q},${t.end_r})` : `Hex (${t.end_q},${t.end_r})`, isPlanet: !!t.end_planet_id, isSun: !!t.end_sun_hex_key, sunHexKey: t.end_sun_hex_key },
    ships: SHIP_COLUMNS.map(col => (t.ships[col] as number) ?? 0),
    travelMode: t.travel_mode, route: t.route ? t.route.map(([q, r]: number[]) => ({ q, r })) : null,
    totalHops: t.total_hops, currentHop: t.current_hop, progress: t.progress,
    startedAt: t.started_at, estimatedArrivalAt: t.estimated_arrival_at, arrived: t.arrived,
    status: (t.status as 'traveling' | 'stationed') ?? 'traveling',
  };
}
